import BulbIcon from '@elementor/icons/BulbIcon';
import AccordionActions from '@elementor/ui/AccordionActions';
import Alert from '@elementor/ui/Alert';
import AlertTitle from '@elementor/ui/AlertTitle';
import Box from '@elementor/ui/Box';
import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useMemo } from 'react';
import infoTipThumbnail from '@assets/image/thumb-color-contrast.png';
import infoTipVideoSrc from '@assets/video/color-contrast.webm';
import FixButton from '@components/fix-button';
import GetFreeInfoTip from '@components/get-free-info-tip';
import { useColorContrastForm } from '@hooks/use-color-contrast-form';
import { StyledBox } from '@styles/app.styles';
import {
	StyledAccordionDetails,
	StyledFrame,
	StyledFrameText,
	StyledIssueResolveHeading,
} from '@styles/manual-fixes.styles';
import { scannerItem } from '@types/scanner-item';
import {
	calcContrastFromHex,
	checkContrastAA,
	isLargeText,
} from '@utils/calc-color-ratio';
import { rgbOrRgbaToHex } from '@utils/color-contrast-helpers';
import { __, sprintf } from '@utils/i18n';
import { queryIframeWindow } from '@utils/query';
import ColorSet from './color-set';
import ColorSetDisabled from './color-set-disabled';
import ContrastLevel from './contrast-level';
import ParentSelector from './parent-selector';

const ColorContrastForm = memo(({ item, formData, onUpdateFormData }) => {
	const colorRef = useRef(null);

	const color = useMemo(
		() =>
			formData?.color ||
			item.messageArgs[3] ||
			rgbOrRgbaToHex(
				queryIframeWindow()
					.getComputedStyle(item.node)
					.getPropertyValue('color'),
			),
		[formData?.color, item.messageArgs, item.node],
	);

	const background = useMemo(
		() => formData?.background || item.messageArgs[4],
		[formData?.background, item.messageArgs],
	);

	const parents = useMemo(
		() => formData?.parents || [item.path.dom],
		[formData?.parents, item.path.dom],
	);

	const backgroundChanged = formData?.backgroundChanged || false;

	const { changeColor, changeBackground, setParentSmaller, setParentLarger } =
		useColorContrastForm({
			item,
			parents,
			color,
			background,
			onUpdateFormData,
		});

	useEffect(() => {
		if (item?.node) {
			colorRef.current = rgbOrRgbaToHex(
				queryIframeWindow()
					.getComputedStyle(item.node)
					.getPropertyValue('color'),
			);
		}
	}, [item]);

	const isBackgroundEnabled =
		item.messageArgs[3] && item.messageArgs[4] && !item.isPotential;

	const colorData = useMemo(() => {
		if (color && background) {
			const large = isLargeText(item.node);
			const result = calcContrastFromHex(color, background, large);

			if (result) {
				return {
					...result,
					isPotential: item.isPotential || false,
				};
			}
		}

		return checkContrastAA(item.node);
	}, [color, background, item.node, item.isPotential]);

	return (
		<>
			<StyledAccordionDetails>
				<ContrastLevel colorData={colorData} />

				<Box>
					<StyledIssueResolveHeading variant="subtitle2" as="h5">
						<BulbIcon fontSize="small" />

						{__('How to resolve')}
					</StyledIssueResolveHeading>

					<StyledFrame>
						<StyledFrameText variant="body2">
							{__(
								'Use the sliders to explore color combinations that meet accessibility standards.',
							)}
						</StyledFrameText>

						<StyledBox>
							<ColorSet
								title={__('Text')}
								color={color}
								initialColor={colorRef.current || color}
								setColor={changeColor}
								area="color"
							/>

							{isBackgroundEnabled ? (
								<ColorSet
									title={__('Background')}
									color={background}
									initialColor={item.messageArgs[4]}
									setColor={changeBackground}
									area="background"
								/>
							) : (
								<ColorSetDisabled
									title={__('Background')}
									description={__(
										'Image and gradient backgrounds must be changed in the design',
									)}
								/>
							)}

							{backgroundChanged && (
								<ParentSelector
									parents={parents}
									setParentSmaller={setParentSmaller}
									setParentLarger={setParentLarger}
								/>
							)}

							<Alert severity={colorData.passesAA ? 'success' : 'error'}>
								<AlertTitle sx={{ mr: 1 }}>
									{sprintf(__('Contrast level: %s'), colorData.ratio)}
								</AlertTitle>
							</Alert>
						</StyledBox>
					</StyledFrame>
				</Box>
			</StyledAccordionDetails>

			<AccordionActions sx={{ pt: 1, pb: 3 }}>
				<FixButton
					infoTipContent={
						<GetFreeInfoTip
							videoSrc={infoTipVideoSrc}
							posterSrc={infoTipThumbnail}
							title={__('Improve color contrast in seconds')}
							description={__(
								'Adjust your colors and apply accessible combinations instantly with Ally.',
							)}
						/>
					}
				/>
			</AccordionActions>
		</>
	);
});

ColorContrastForm.displayName = 'ColorContrastForm';

ColorContrastForm.propTypes = {
	item: scannerItem,
	formData: PropTypes.object,
	onUpdateFormData: PropTypes.func.isRequired,
};

export default ColorContrastForm;
